import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import {
  AiTaskLineageBlock,
  TaskContextLinkedAsset,
} from '../../models/ai-assistant.models';
import { AssetDisplayComponent } from '../asset-display/asset-display.component';
import { ActionItemDisplayComponent } from '../action-item-display/action-item-display.component';

export interface LineageRow {
  depth: number;
  type: 'risk' | 'edge' | 'task' | 'action' | 'asset' | 'more';
  label: string;
  badge?: string;
  isLast: boolean;
  /** One boolean per ancestor before the connector: true = draw │, false = draw space */
  continues: boolean[];
}

@Component({
  selector: 'app-response-task-lineage',
  standalone: true,
  imports: [CommonModule, AssetDisplayComponent, ActionItemDisplayComponent],
  templateUrl: './response-task-lineage.component.html',
  styleUrl: './response-task-lineage.component.css',
})
export class ResponseTaskLineageComponent implements OnChanges {
  @Input({ required: true }) block!: AiTaskLineageBlock;

  assetViewMode: 'chip' | 'table' = 'table';
  actionItemViewMode: 'chip' | 'table' = 'table';

  private readonly sanitizer = inject(DomSanitizer);

  ngOnChanges(_c: SimpleChanges): void {}

  // ── LLM narration (contract-driven) ──────────────────────────────────────
  // block.sectionBlocks is built by the response mapper from trace_task_lineage's
  // own uiSections keys (task_overview, linked_risks, next_step, ...) — populated
  // either from the backend's multi-tool sectionProse map, or the single-tool
  // narration answer. Falls back to the deterministic getters below only when no
  // LLM prose is available for that section (e.g. narration fallback occurred).

  private sectionProse(key: string): string {
    return this.block.sectionBlocks?.find((s) => s.key === key)?.prose ?? '';
  }

  get taskOverviewProse(): string {
    return this.sectionProse('task_overview') || this.introSummary;
  }

  get linkedRiskProse(): string {
    return this.sectionProse('linked_risks');
  }

  get nextStepProse(): string {
    return this.sectionProse('next_step') || this.nextStep;
  }

  proseHtml(text: string): SafeHtml {
    const html = marked(text, { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // ── Task overview ─────────────────────────────────────────────────────────

  get taskFields(): Record<string, string> {
    const t = (this.block as unknown as Record<string, unknown>)['task'] as Record<string, unknown> ?? {};
    const arr = (v: unknown) => Array.isArray(v) ? (v as string[]).join(', ') : String(v ?? '');
    return {
      control:      String(t['control'] ?? t['taskId'] ?? ''),
      title:        String(t['title'] ?? t['name'] ?? t['control'] ?? ''),
      description:  String(t['description'] ?? ''),
      status:       String(t['status'] ?? ''),
      priority:     String(t['priority'] ?? ''),
      risk:         String(t['risk'] ?? ''),
      domain:       String(t['domain'] ?? ''),
      subdomain:    String(t['subdomain'] ?? ''),
      dueDate:      String(t['dueDate'] ?? ''),
      frameworks:   arr(t['frameworks']),
      technologies: arr(t['technologies']),
      assigned:     String(t['assigned'] ?? t['assignee'] ?? t['owner'] ?? ''),
    };
  }

  // ── Risks ────────────────────────────────────────────────────────────────

  get riskRows(): { riskId: string; title: string; description: string; riskLevel: string; priority: string; status: string }[] {
    return (this.block.linkedRisks ?? []).map(r => {
      const raw = r as unknown as Record<string, unknown>;
      const title = String(raw['title'] ?? raw['name'] ?? raw['riskName'] ?? r.description ?? '—').slice(0, 80);
      return {
        riskId:      String(r.riskId ?? '—'),
        title,
        description: String(r.description ?? '—'),
        riskLevel:   String(r.riskLevel ?? ''),
        priority:    String(r.priority ?? ''),
        status:      String(r.status ?? ''),
      };
    });
  }

  // ── Frameworks ───────────────────────────────────────────────────────────

  get frameworks(): string[] {
    const taskData = (this.block as unknown as Record<string, unknown>)['task'] as Record<string, unknown> | undefined;
    const raw = taskData?.['frameworks'];
    if (Array.isArray(raw)) return (raw as unknown[]).map(f => String(f)).filter(Boolean);
    if (typeof raw === 'string' && raw) return raw.split(',').map(f => f.trim()).filter(Boolean);
    return [];
  }

  fwPillClass(i: number): string {
    return ['tl-fw-pill--green', 'tl-fw-pill--blue', 'tl-fw-pill--purple'][i % 3];
  }

  // ── Asset categories ─────────────────────────────────────────────────────

  get assetCategoryRows(): { category: string; count: number }[] {
    const by = this.block.assetSummary?.byCategory ?? {};
    return Object.entries(by).map(([category, count]) => ({ category, count }));
  }

  // ── Lineage tree ─────────────────────────────────────────────────────────

  private static readonly MAX_ACTIONS = 8;
  private static readonly MAX_ASSETS_PER_ACTION = 5;

  get lineageRows(): LineageRow[] {
    const rows: LineageRow[] = [];
    const risk    = this.riskRows[0] ?? null;
    const actions = this.block.linkedActionItems ?? [];
    const ctrl    = this.taskFields['control'];

    // Group action items by ID so one action item → many assets renders correctly.
    // Key: actionItemId (or text as fallback). Value: label + collected asset tags.
    type Group = { label: string; assets: Array<{ tag: string; name: string }> };
    const actionMap = new Map<string, Group>();
    for (const ai of actions) {
      // Group by text (primary) — backend sends one row per (action_item × asset),
      // so actionItemId is unique per row and cannot be used as the group key.
      const key   = (ai.text ?? ai.actionItemId ?? '').slice(0, 200) || String(Math.random());
      const label = (ai.text ?? ai.actionItemId ?? '').slice(0, 80);
      const tag   = (ai.assetId ?? ai.assetTag ?? '').trim();
      const name  = (ai.assetName ?? '').trim();
      if (!actionMap.has(key)) actionMap.set(key, { label, assets: [] });
      const g = actionMap.get(key)!;
      if (tag && !g.assets.some(a => a.tag === tag)) g.assets.push({ tag, name });
    }

    const groups       = Array.from(actionMap.values());
    const totalActions = this.block.totals?.actionItemCount ?? groups.length;
    const shownGroups  = groups.slice(0, ResponseTaskLineageComponent.MAX_ACTIONS);
    const extraActions = Math.max(0, totalActions - shownGroups.length);
    const hasActions   = groups.length > 0;

    // Depth layout
    const riskD    = 0;
    const riskEdgD = 1;
    const taskD    = risk ? 2 : 0;
    const taskEdgD = taskD + 1;
    const actD     = taskEdgD + 1;
    const asstD    = actD + 1;

    const pre = (depth: number): boolean[] =>
      depth > 1 ? new Array(depth - 1).fill(false) : [];

    // Risk root
    if (risk) {
      rows.push({
        depth: riskD, type: 'risk',
        label: `${risk.riskId}  ${(risk.title || risk.description).slice(0, 60)}`,
        badge: risk.riskLevel || undefined,
        isLast: false, continues: [],
      });
      rows.push({
        depth: riskEdgD, type: 'edge',
        label: 'HAS_REMEDIATION_TASK',
        isLast: true, continues: [],
      });
    }

    // Task
    rows.push({
      depth: taskD, type: 'task',
      label: ctrl,
      isLast: !hasActions, continues: pre(taskD),
    });

    if (!hasActions) return rows;

    // Edge to action items
    rows.push({
      depth: taskEdgD, type: 'edge',
      label: 'HAS_ACTION_ITEM',
      isLast: true, continues: pre(taskEdgD),
    });

    // Action items (capped at MAX_ACTIONS)
    shownGroups.forEach((group, idx) => {
      const isLastAction = idx === shownGroups.length - 1 && extraActions === 0;
      rows.push({
        depth: actD, type: 'action',
        label: group.label,
        isLast: isLastAction, continues: pre(actD),
      });

      // Assets for this action item (capped at MAX_ASSETS_PER_ACTION)
      const MAX_A = ResponseTaskLineageComponent.MAX_ASSETS_PER_ACTION;
      const shownAssets = group.assets.slice(0, MAX_A);
      const moreAssets  = group.assets.length - shownAssets.length;

      shownAssets.forEach((asset, aIdx) => {
        const isLastAsset = aIdx === shownAssets.length - 1 && moreAssets === 0;
        const c = pre(asstD);
        // At the action depth position, pipe continues if action has more siblings
        if (c.length > 0) c[actD - 1] = !isLastAction;
        rows.push({
          depth: asstD, type: 'asset',
          label: `APPLIES_TO_ASSET  →  ${asset.tag}${asset.name ? '  ─  ' + asset.name.slice(0, 28) : ''}`,
          isLast: isLastAsset, continues: c,
        });
      });

      if (moreAssets > 0) {
        const c = pre(asstD);
        if (c.length > 0) c[actD - 1] = !isLastAction;
        rows.push({
          depth: asstD, type: 'more',
          label: `+${moreAssets} more asset${moreAssets > 1 ? 's' : ''}`,
          isLast: true, continues: c,
        });
      }
    });

    if (extraActions > 0) {
      rows.push({
        depth: actD, type: 'more',
        label: `+${extraActions} more action item${extraActions > 1 ? 's' : ''}`,
        isLast: true, continues: pre(actD),
      });
    }

    return rows;
  }

  // ── Deterministic intro (fallback when no LLM prose is available) ────────

  get introSummary(): string {
    const t = this.block.totals;
    const fw = this.frameworks.length;
    if (!t) return '';
    const parts: string[] = [];
    if (t.riskCount)       parts.push(`${t.riskCount} risk`);
    if (t.actionItemCount) parts.push(`${t.actionItemCount} action items`);
    if (t.assetCount)      parts.push(`${t.assetCount} assets`);
    if (fw)                parts.push(`${fw} framework${fw > 1 ? 's' : ''}`);
    if (!parts.length) return '';
    return `Task ${this.taskFields['control']} is linked to ${parts.join(', ')}.`;
  }

  // ── Deterministic next step (fallback when no LLM prose is available) ────

  get nextStep(): string {
    const t = this.block.totals;
    const ctrl = this.taskFields['control'];
    if (!t) return '';

    const openActions = t.actionItemCount ?? 0;
    const assetCount  = t.assetCount ?? 0;

    if (openActions > 0 && assetCount > 0) {
      return `Review the ${openActions} open action item${openActions > 1 ? 's' : ''} for ${ctrl} and ensure each linked asset (${assetCount} total) has evidence assigned before the next audit cycle.`;
    }
    if (openActions > 0) {
      return `${openActions} action item${openActions > 1 ? 's are' : ' is'} pending for ${ctrl}. Assign owners and target dates to progress toward compliance closure.`;
    }
    if (assetCount > 0) {
      return `${ctrl} covers ${assetCount} asset${assetCount > 1 ? 's' : ''}. Verify that relevant evidence has been collected for each linked asset.`;
    }
    return `No open action items or assets are currently linked to ${ctrl}. Consider reviewing the risk register for this control.`;
  }

  // ── Dynamic section numbering ─────────────────────────────────────────────

  private get visibleSectionKeys(): string[] {
    const keys: string[] = [];
    if (this.riskRows.length > 0)                              keys.push('risk');
    if (this.frameworks.length > 0)                            keys.push('frameworks');
    if (this.assetCategoryRows.length > 0)                     keys.push('categories');
    if ((this.block.linkedActionItems?.length ?? 0) > 0)       keys.push('actions');
    if ((this.block.linkedAssets?.length ?? 0) > 0)            keys.push('assets');
    if (this.nextStep)                                         keys.push('next');
    return keys;
  }

  sectionNum(key: string): number {
    return this.visibleSectionKeys.indexOf(key) + 1;
  }

  // ── Shared helpers ────────────────────────────────────────────────────────

  badgeClass(value: string): string {
    return `ai-pill tone-${(value || '').toLowerCase().replace(/[\s_]+/g, '-')}`;
  }
}
